<!-- Price Tags Management -->
<div class="price-tags-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fas fa-tags"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Price Tags</h1>
          <p class="page-description">Manage and create QR code price tags for your menu items</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary btn-sm" (click)="listPriceTags()" [disabled]="isLoadingPriceTags">
          <i class="fas fa-sync-alt" [class.fa-spin]="isLoadingPriceTags"></i>
          Refresh
        </button>
        <button class="btn btn-primary" (click)="generateBarcode(content)" [disabled]="isLoadingPriceTags">
          <i class="fas fa-plus"></i>
          Create New Tag
        </button>
      </div>
    </div>
  </div>

  <!-- Filters Panel -->
  <div class="filters-panel">
    <div class="search-section">
      <div class="search-box">
        <div class="search-input-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input type="text"
                 class="search-input"
                 placeholder="Search by tag name or status..."
                 [(ngModel)]="searchTerm">
          <button *ngIf="searchTerm"
                  class="clear-search"
                  (click)="searchTerm = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-tags"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ filteredPriceTags?.length || 0 }}</div>
          <div class="stat-label">Total Tags</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Price Tags Table -->
  <div class="data-panel">
    <div class="panel-header">
      <div class="panel-title">
        <div class="title-icon">
          <i class="fas fa-list-alt"></i>
        </div>
        <div>
          <div class="title-text">Price Tags List</div>
          <div class="title-subtitle">Manage your QR code price tags and their status</div>
        </div>
      </div>
    </div>

    <div class="panel-body">
      @if (isLoadingPriceTags) {
        <div class="loading-state">
          <div class="loading-content">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
            </div>
            <div class="loading-text">
              <h5>Loading Price Tags</h5>
              <p>Please wait while we fetch your price tag data...</p>
            </div>
          </div>
        </div>
      } @else {
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>Tag Name</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (item of filteredPriceTags; track item.id) {
                <tr>
                  <td>
                    <div class="d-flex align-items-center gap-3">
                      <div style="width: 32px; height: 32px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px;">
                        <i class="fas fa-qrcode"></i>
                      </div>
                      <div>
                        <div style="font-size: 13px; font-weight: 500; color: var(--text-primary);">{{ item.name }}</div>
                        <div style="font-size: 12px; color: var(--text-tertiary);">ID: {{ item.id }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-dot"
                          [class.badge-status-active]="item.status === 'active'"
                          [class.badge-status-inactive]="item.status !== 'active'">
                      {{ item.status | titlecase }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex gap-2">
                      <button class="btn btn-outline-secondary btn-sm"
                              (click)="editTag(item, content)"
                              [disabled]="deletingTagId === item.id">
                        <i class="fas fa-edit"></i>
                        Edit
                      </button>
                      <button class="btn btn-danger btn-sm"
                              (click)="delete(item.id)"
                              [disabled]="deletingTagId === item.id">
                        <i class="fas fa-trash" *ngIf="deletingTagId !== item.id"></i>
                        <i class="fas fa-spinner fa-spin" *ngIf="deletingTagId === item.id"></i>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="3">
                    <div class="empty-state">
                      @if (searchTerm) {
                        <div class="empty-icon">
                          <i class="fas fa-search fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Tags Found</h5>
                          <p>No price tags match your search criteria "{{ searchTerm }}". Try adjusting your search terms.</p>
                          <button class="btn btn-outline-secondary" (click)="searchTerm = ''">
                            <i class="fas fa-times me-1"></i>Clear Search
                          </button>
                        </div>
                      } @else {
                        <div class="empty-icon">
                          <i class="fas fa-tags fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Price Tags Yet</h5>
                          <p>Get started by creating your first price tag. Price tags help customers access menu information via QR codes.</p>
                          <button class="btn btn-primary" (click)="generateBarcode(content)">
                            <i class="fas fa-plus me-1"></i>
                            Create First Tag
                          </button>
                        </div>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</div>

<!-- Create/Edit Modal Template -->
<ng-template #content let-modal>
  <div class="modal-header">
    <div class="d-flex align-items-center gap-3">
      <button class="btn btn-outline-secondary btn-sm" (click)="modal.close('close-click')" style="padding: 4px 8px;">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="d-flex align-items-center gap-3">
        <div style="width: 36px; height: 36px; border-radius: var(--radius-md); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 15px;">
          <i class="fas fa-tag"></i>
        </div>
        <div>
          <h4 class="modal-title" style="margin: 0;">{{ isEditing ? 'Edit Price Tag' : 'Create New Price Tag' }}</h4>
          <p style="font-size: 12px; color: var(--text-tertiary); margin: 2px 0 0;">{{ isEditing ? 'Update the details of your price tag' : 'Create a new QR code price tag for your menu' }}</p>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-body">
    <form [formGroup]="generateForm" (ngSubmit)="onsubmit()">
      <div class="form-group">
        <label class="form-label">
          <i class="fas fa-tag me-1"></i>
          Tag Name <span style="color: var(--danger-text);">*</span>
        </label>
        <input type="text"
               class="form-control"
               formControlName="name"
               placeholder="Enter a descriptive name for your price tag"
               [class.is-invalid]="generateForm.get('name')?.invalid && generateForm.get('name')?.touched">
        <div *ngIf="generateForm.get('name')?.invalid && generateForm.get('name')?.touched"
             class="invalid-feedback">
          Tag name is required
        </div>
        <small style="color: var(--text-tertiary); margin-top: 4px; display: block;">
          Choose a clear, descriptive name that helps identify this price tag
        </small>
      </div>

      <div class="modal-footer" style="padding: 16px 0 0; border-top: none;">
        <button type="button"
                class="btn btn-outline-secondary"
                (click)="modal.close('cancel')"
                [disabled]="isLoading">
          Cancel
        </button>
        <button type="submit"
                class="btn btn-primary"
                [disabled]="generateForm.invalid || isLoading">
          @if (isLoading) {
            <i class="fas fa-spinner fa-spin me-1"></i>
            {{ isEditing ? 'Updating...' : 'Creating...' }}
          } @else {
            <i class="fas {{ isEditing ? 'fa-save' : 'fa-plus' }} me-1"></i>
            {{ isEditing ? 'Update Tag' : 'Create Tag' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
